import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { IconMessage, IconX, IconSettings, IconUpload, IconPrinter, IconDownload, IconHistory, IconSave, IconEye, IconRefresh, IconBroadcast } from '../../Components/Icons/Icons';
import { usePageTitle } from '../../Hooks/usePageTitle';
import { useParams, useNavigate } from 'react-router-dom';
import { LoadingScreen } from '../../Components/LoadingScreen/LoadingScreen';
import './CueInput.css';
import logo from '../../Assets/Logo/LIVECUE-Logo.png';
import { Project } from '../../Interfaces/Project/Project';
import { Cue } from '../../Interfaces/Cue/Cue';
import { CustomField, DEFAULT_FIELDS } from '../../Interfaces/CustomField/CustomField';
import { ImportWizard, toDateTimeString, ParsedCue } from './ImportWizard';
import { PrintableCueSheet } from '../../Components/PrintableCueSheet/PrintableCueSheet';
import { exportCuesToCsv } from '../../utils/exportCsv';
import { usePlan } from '../../Hooks/usePlan';
import { UpgradeModal, UpgradeFeature } from '../../Components/UpgradeModal/UpgradeModal';
import dayjs from 'dayjs';
import { db, collection, addDoc, query, where, doc, updateDoc, deleteDoc, onSnapshot, auth } from '../../Backend/firebase';
import { CueComment } from '../../Interfaces/Comment/CueComment';
import { ProjectSnapshot, SnapshotCue } from '../../Interfaces/Snapshot/ProjectSnapshot';
import { debounce } from 'lodash';
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors, DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext, verticalListSortingStrategy, horizontalListSortingStrategy, useSortable, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface CueInputProps { projects: Project[]; }

const toTimeInput = (iso: string): string => {
  try { return dayjs(iso).format('HH:mm'); } catch { return ''; }
};

const fromTimeInput = (timeStr: string, existingISO: string): string => {
  if (!timeStr) return existingISO;
  const [h, m] = timeStr.split(':').map(Number);
  return dayjs(existingISO).hour(h).minute(m).second(0).millisecond(0).toISOString();
};

function AutoTextarea({ value, onChange, placeholder, className, ariaLabel }: {
  value: string; onChange: (v: string) => void; placeholder?: string; className?: string; ariaLabel?: string;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);
  useLayoutEffect(() => {
    if (ref.current) {
      ref.current.style.height = 'auto';
      ref.current.style.height = ref.current.scrollHeight + 'px';
    }
  }, [value]);
  return (
    <textarea
      ref={ref}
      className={className}
      placeholder={placeholder}
      aria-label={ariaLabel}
      value={value}
      rows={1}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

function SortableFieldHeader({ field, onRemove }: {
  field: CustomField;
  onRemove: (id: string) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: field.id });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 }}
      className="ci-col-field ci-th"
    >
      <div className="ci-col-drag-handle" {...attributes} {...listeners} title="Drag to reorder column">⠿</div>
      <span>{field.label}</span>
      <button className="ci-th-remove" onClick={() => onRemove(field.id)} title={`Remove ${field.label}`}>×</button>
    </div>
  );
}

// Auto-timing: after the cue at startIndex changes, shift every downstream cue
// so each starts when the previous ends, preserving each cue's own duration.
function cascadeFrom(cues: Cue[], startIndex: number): Cue[] {
  const result = [...cues];
  for (let i = Math.max(1, startIndex + 1); i < result.length; i++) {
    const prev = result[i - 1];
    const curr = result[i];
    const duration = Math.max(0, new Date(curr.endTime).getTime() - new Date(curr.startTime).getTime());
    const newStart = prev.endTime;
    const newEnd = new Date(new Date(newStart).getTime() + duration).toISOString();
    result[i] = { ...curr, startTime: newStart, endTime: newEnd };
  }
  return result;
}

function SortableCueRow({
  cue, index, fields, isLast, dragEnabled, onInputChange, onTimeChange, onDelete, onOpenComments, commentCount,
}: {
  cue: Cue; index: number; fields: CustomField[]; isLast: boolean; dragEnabled: boolean;
  onInputChange: (index: number, fieldId: string, value: string) => void;
  onTimeChange: (index: number, field: 'startTime' | 'endTime', timeStr: string) => void;
  onDelete: (id: string) => void;
  onOpenComments: (id: string) => void;
  commentCount: number;
}) {
  // Strip seconds/ms from both ends before diffing so partial minutes don't skew the display
  const durationMinutes = Math.max(0, Math.round(
    (dayjs(cue.endTime).startOf('minute').valueOf() - dayjs(cue.startTime).startOf('minute').valueOf()) / 60000
  ));
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: cue.id });

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 }}
      className={`ci-row${cue.isLive ? ' ci-row-live' : ''}`}
    >
      {/* Timeline spine — sticky left:0 */}
      <div className="ci-col-spine">
        <span className="ci-spine-time">{dayjs(cue.startTime).format('h:mm')}</span>
        <div className="ci-spine-track">
          <div className={`ci-dot${cue.isLive ? ' ci-dot-live' : ''}`} />
          {!isLast && <div className="ci-spine-connector" />}
        </div>
      </div>

      {/* Title — sticky left:72px */}
      <div className={`ci-col-title${cue.isLive ? ' ci-col-title-live' : ''}`}>
        <div
          className={`ci-drag${dragEnabled ? '' : ' ci-drag--locked'}`}
          title={dragEnabled ? "Drag to reorder" : "Upgrade to Pro to reorder cues"}
          {...(dragEnabled ? { ...attributes, ...listeners } : {})}
        >⠿</div>
        <div className="ci-num">{cue.cueNumber}</div>
        <AutoTextarea
          className="ci-title-input"
          placeholder="Untitled cue"
          ariaLabel={`Cue ${cue.cueNumber} title`}
          value={cue.title}
          onChange={(v) => onInputChange(index, 'title', v)}
        />
      </div>

      {/* Start time */}
      <div className="ci-col-field">
        <input
          className="ci-time-input"
          type="time"
          aria-label={`Cue ${cue.cueNumber} start time`}
          value={toTimeInput(cue.startTime)}
          onChange={(e) => onTimeChange(index, 'startTime', e.target.value)}
        />
      </div>

      {/* End time */}
      <div className="ci-col-field">
        <input
          className="ci-time-input"
          type="time"
          aria-label={`Cue ${cue.cueNumber} end time`}
          value={toTimeInput(cue.endTime)}
          onChange={(e) => onTimeChange(index, 'endTime', e.target.value)}
        />
      </div>

      {/* Duration */}
      <div className="ci-col-duration">
        <input
          key={`${cue.id}-dur-${durationMinutes}`}
          className="ci-duration-input"
          type="number"
          min="0"
          aria-label={`Cue ${cue.cueNumber} duration in minutes`}
          defaultValue={durationMinutes}
          onBlur={(e) => {
            const m = parseInt(e.target.value, 10);
            if (!isNaN(m) && m >= 0)
              onTimeChange(index, 'endTime', dayjs(cue.startTime).add(m, 'minute').format('HH:mm'));
          }}
          onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
        />
        <span className="ci-duration-unit">min</span>
      </div>

      {/* Dynamic fields */}
      {fields.map((field) => (
        <div key={field.id} className="ci-col-field">
          {field.type === 'time' ? (
            <input
              className="ci-time-input"
              type="time"
              aria-label={`${field.label} for cue ${cue.cueNumber}`}
              value={cue.fieldValues[field.id] ? toTimeInput(cue.fieldValues[field.id]) : ''}
              onChange={(e) => {
                if (e.target.value)
                  onInputChange(index, field.id, fromTimeInput(e.target.value, cue.startTime));
              }}
            />
          ) : (
            <AutoTextarea
              className="ci-field-input"
              placeholder="—"
              ariaLabel={`${field.label} for cue ${cue.cueNumber}`}
              value={cue.fieldValues[field.id] || ''}
              onChange={(v) => onInputChange(index, field.id, v)}
            />
          )}
        </div>
      ))}

      <div className="ci-col-del">
        <button
          className={`ci-cmt-btn${commentCount > 0 ? ' ci-cmt-btn--has' : ''}`}
          onClick={() => onOpenComments(cue.id)}
          title="Comments"
          aria-label={`Comments on cue ${cue.cueNumber}`}
        ><IconMessage size={15} />{commentCount > 0 ? <span className="ci-cmt-count">{commentCount}</span> : null}</button>
        <button className="ci-del-btn" onClick={() => onDelete(cue.id)} title="Delete cue" aria-label={`Delete cue ${cue.cueNumber}`}><IconX size={15} /></button>
      </div>
    </div>
  );
}

function CueInput({ projects }: CueInputProps) {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const [cues, setCues] = useState<Cue[]>([]);
  const [project, setProject] = useState<Project | null>(null);
  const [comments, setComments] = useState<CueComment[]>([]);
  const [commentsCueId, setCommentsCueId] = useState<string | null>(null);
  const [commentText, setCommentText] = useState('');
  const [autoTiming, setAutoTiming] = useState(false);
  const [snapshots, setSnapshots] = useState<ProjectSnapshot[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [restoring, setRestoring] = useState(false);
  usePageTitle(project ? project.title : "Cue Editor");
  const [fields, setFields] = useState<CustomField[]>(DEFAULT_FIELDS);
  const [showFieldModal, setShowFieldModal] = useState(false);
  const [showAIImport, setShowAIImport] = useState(false);
  const [upgradeFeature, setUpgradeFeature] = useState<UpgradeFeature | null>(null);

  const uid = (JSON.parse(sessionStorage.getItem('CURRENT_USER') || 'null'))?.id ?? null;
  const { plan, canAddCue, canUseCustomFields, canDragReorder, canUseAIImport, canEdit, teamRole } = usePlan(uid);
  const [newFieldLabel, setNewFieldLabel] = useState('');
  const [newFieldType, setNewFieldType] = useState<'text' | 'time'>('text');
  const [deleteCueId, setDeleteCueId] = useState<string | null>(null);
  const cueToDelete = cues.find(c => c.id === deleteCueId);
  const [loading, setLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState<'saved' | 'saving' | 'error'>('saved');
  // Ref so the debounced function can always access the latest setter without stale closure
  const setSaveStatusRef = useRef(setSaveStatus);
  setSaveStatusRef.current = setSaveStatus;

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  // Cue IDs the local user is actively editing — protected from being clobbered
  // by incoming real-time updates from collaborators (see the cues listener).
  const pendingRef = useRef<Set<string>>(new Set());

  // Project metadata (title, fields)
  useEffect(() => {
    if (!projectId) return;
    const found = projects.find((p) => p.firebaseID === projectId);
    if (found) {
      setProject(found);
      setFields(found.fields?.length ? found.fields : DEFAULT_FIELDS);
      setAutoTiming(found.autoTiming ?? false);
    }
  }, [projectId, projects]);

  const toggleAutoTiming = async () => {
    if (!canEdit || !projectId) return;
    const next = !autoTiming;
    setAutoTiming(next);
    try { await updateDoc(doc(db, 'projects', projectId), { autoTiming: next }); }
    catch { setAutoTiming(!next); }
  };

  // Real-time cues — live multi-user sync. A cue the local user is mid-edit on
  // (in pendingRef) keeps its local value so remote updates don't overwrite
  // in-progress typing; every other cue reflects the latest from Firestore.
  useEffect(() => {
    if (!projectId) return;
    const q = query(collection(db, 'cues'), where('projectRef', '==', projectId));
    const unsub = onSnapshot(q, (snap) => {
      const remote: Cue[] = snap.docs.map((d) => {
        const data = d.data();
        const fieldValues: Record<string, string> = data.fieldValues || {};
        if (!data.fieldValues) {
          ['presenter', 'location', 'avMedia', 'audioSource', 'sideScreens', 'centerScreen', 'lighting', 'ambientLights', 'notes']
            .forEach((k) => { if (data[k]) fieldValues[k] = data[k]; });
        }
        return {
          id: d.id,
          cueNumber: data.cueNumber,
          title: data.title || '',
          startTime: data.startTime?.toDate ? data.startTime.toDate().toISOString() : (data.startTime || new Date().toISOString()),
          endTime: data.endTime?.toDate ? data.endTime.toDate().toISOString() : (data.endTime || new Date().toISOString()),
          projectRef: data.projectRef,
          isLive: data.isLive ?? false,
          fieldValues,
        } as Cue;
      }).sort((a, b) => a.cueNumber - b.cueNumber);

      setCues((prev) => {
        const prevById = new Map(prev.map((c) => [c.id, c]));
        return remote.map((r) =>
          pendingRef.current.has(r.id) && prevById.has(r.id) ? prevById.get(r.id)! : r
        );
      });
      setLoading(false);
    }, (err) => { console.error('Error syncing cues:', err); setLoading(false); });
    return unsub;
  }, [projectId]);

  // Real-time comments for the whole project (drives per-cue counts + the panel)
  useEffect(() => {
    if (!projectId) return;
    const q = query(collection(db, 'comments'), where('projectRef', '==', projectId));
    return onSnapshot(q, (snap) => {
      setComments(snap.docs.map((d) => ({ id: d.id, ...d.data() } as CueComment))
        .sort((a, b) => a.createdAt.localeCompare(b.createdAt)));
    }, () => {});
  }, [projectId]);

  const addComment = async () => {
    const text = commentText.trim();
    if (!text || !commentsCueId || !projectId || !auth.currentUser) return;
    const authorName = auth.currentUser.displayName || auth.currentUser.email || 'Someone';
    setCommentText('');
    try {
      await addDoc(collection(db, 'comments'), {
        projectRef: projectId,
        cueId: commentsCueId,
        uid: auth.currentUser.uid,
        authorName,
        text,
        createdAt: new Date().toISOString(),
      });
    } catch (err) { console.error('Error posting comment:', err); }
  };

  const deleteComment = async (id: string) => {
    try { await deleteDoc(doc(db, 'comments', id)); }
    catch (err) { console.error('Error deleting comment:', err); }
  };

  // Saved versions (revision history)
  useEffect(() => {
    if (!projectId) return;
    const q = query(collection(db, 'snapshots'), where('projectRef', '==', projectId));
    return onSnapshot(q, (snap) => {
      setSnapshots(snap.docs.map((d) => ({ id: d.id, ...d.data() } as ProjectSnapshot))
        .sort((a, b) => b.createdAt.localeCompare(a.createdAt)));
    }, () => {});
  }, [projectId]);

  const saveVersion = async () => {
    if (!canEdit || !projectId || !auth.currentUser) return;
    const suggested = `Version ${new Date().toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}`;
    const label = window.prompt('Name this version:', suggested);
    if (label === null) return;
    const authorName = auth.currentUser.displayName || auth.currentUser.email || 'Someone';
    const snapCues: SnapshotCue[] = [...cues].sort((a, b) => a.cueNumber - b.cueNumber).map((c) => ({
      cueNumber: c.cueNumber, title: c.title, startTime: c.startTime, endTime: c.endTime,
      isLive: c.isLive, fieldValues: c.fieldValues || {},
    }));
    try {
      await addDoc(collection(db, 'snapshots'), {
        projectRef: projectId, authorName, label: label.trim() || 'Untitled version',
        createdAt: new Date().toISOString(), cues: snapCues,
      });
    } catch (err) { console.error('Error saving version:', err); }
  };

  const restoreVersion = async (snap: ProjectSnapshot) => {
    if (!canEdit || !projectId) return;
    if (!window.confirm(`Restore "${snap.label}"? This replaces all current cues with that version.`)) return;
    setRestoring(true);
    try {
      await Promise.all(cues.map((c) => deleteDoc(doc(db, 'cues', c.id))));
      await Promise.all(snap.cues.map((sc) => addDoc(collection(db, 'cues'), {
        cueNumber: sc.cueNumber, title: sc.title, startTime: sc.startTime, endTime: sc.endTime,
        projectRef: projectId, isLive: false, fieldValues: sc.fieldValues || {},
      })));
      setShowHistory(false);
    } catch (err) { console.error('Error restoring version:', err); }
    finally { setRestoring(false); }
  };

  const deleteVersion = async (id: string) => {
    try { await deleteDoc(doc(db, 'snapshots', id)); }
    catch (err) { console.error('Error deleting version:', err); }
  };

  // Escape closes whichever drawer/modal is open (keyboard accessibility).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (commentsCueId) setCommentsCueId(null);
      else if (showHistory) setShowHistory(false);
      else if (showFieldModal) setShowFieldModal(false);
      else if (deleteCueId) setDeleteCueId(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [commentsCueId, showHistory, showFieldModal, deleteCueId]);

  const debouncedUpdate = useRef(
    debounce(async (cue: Cue) => {
      try {
        await updateDoc(doc(db, 'cues', cue.id), {
          title: cue.title,
          startTime: cue.startTime,
          endTime: cue.endTime,
          fieldValues: cue.fieldValues,
          isLive: cue.isLive,
        });
        setSaveStatusRef.current('saved');
      } catch (err) {
        console.error('Error updating cue:', err);
        setSaveStatusRef.current('error');
      } finally {
        // Local edit persisted — let remote updates for this cue through again.
        pendingRef.current.delete(cue.id);
      }
    }, 800)
  ).current;

  // Flush pending writes before the component unmounts (e.g. navigating away)
  useEffect(() => () => { debouncedUpdate.flush(); }, [debouncedUpdate]);

  // Writes first-cue startTime and cue count back to the project doc so the homepage reflects changes
  const syncProjectMeta = (updatedCues: Cue[]) => {
    if (!projectId) return;
    updateDoc(doc(db, 'projects', projectId), {
      cueAmount: updatedCues.length,
      ...(updatedCues.length > 0 && { startTime: new Date(updatedCues[0].startTime) }),
    }).catch(err => console.error('Error syncing project meta:', err));
  };

  const handleInputChange = (index: number, fieldId: string, value: string) => {
    if (!canEdit) return;
    setSaveStatus('saving');
    const updated = [...cues];
    if (fieldId === 'title') {
      updated[index] = { ...updated[index], title: value };
    } else {
      updated[index] = { ...updated[index], fieldValues: { ...updated[index].fieldValues, [fieldId]: value } };
    }
    setCues(updated);
    pendingRef.current.add(updated[index].id);
    debouncedUpdate(updated[index]);
  };

  const handleTimeChange = (index: number, field: 'startTime' | 'endTime', timeStr: string) => {
    if (!canEdit) return;
    if (!timeStr) return;
    setSaveStatus('saving');
    let updated = [...cues];
    updated[index] = { ...updated[index], [field]: fromTimeInput(timeStr, updated[index][field]) };

    // Which downstream cues need persisting (their times shifted).
    let downstream: Cue[] = [];
    if (field === 'endTime' && autoTiming) {
      // Cascade: shift every following cue to stay back-to-back.
      updated = cascadeFrom(updated, index);
      downstream = updated.slice(index + 1);
    } else if (field === 'endTime' && index + 1 < updated.length) {
      // Single-step: only the immediate next cue's start follows this end.
      updated[index + 1] = { ...updated[index + 1], startTime: updated[index].endTime };
      downstream = [updated[index + 1]];
    }

    setCues(updated);
    pendingRef.current.add(updated[index].id);
    debouncedUpdate(updated[index]);
    // Downstream cues are written directly — the shared debounce would collapse
    // multiple calls into a single last-wins write.
    downstream.forEach((c) => {
      pendingRef.current.add(c.id);
      updateDoc(doc(db, 'cues', c.id), { startTime: c.startTime, endTime: c.endTime })
        .catch((err) => console.error('Error cascading cue time:', err))
        .finally(() => pendingRef.current.delete(c.id));
    });
    if (index === 0 && field === 'startTime') syncProjectMeta(updated);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    if (!canEdit) return;
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = cues.findIndex((c) => c.id === active.id);
    const newIndex = cues.findIndex((c) => c.id === over.id);
    const reordered = arrayMove(cues, oldIndex, newIndex).map((c, i) => ({
      ...c, cueNumber: i + 1, isLive: i === 0,
    }));
    setCues(reordered);
    syncProjectMeta(reordered);
    await Promise.all(
      reordered.map((c) =>
        updateDoc(doc(db, 'cues', c.id), {
          cueNumber: c.cueNumber, isLive: c.isLive,
        })
      )
    );
  };

  const handleColDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = fields.findIndex(f => f.id === active.id);
    const newIndex = fields.findIndex(f => f.id === over.id);
    const reordered = arrayMove(fields, oldIndex, newIndex);
    setFields(reordered);
    await persistFields(reordered);
  };

  const addCue = async () => {
    if (!canEdit) return;
    if (!canAddCue(cues.length)) { setUpgradeFeature('cues'); return; }
    if (!projectId) return;
    const isFirst = cues.length === 0;
    let startTime = new Date().toISOString();
    let endTime = new Date(Date.now() + 30 * 60000).toISOString();
    if (cues.length > 0) {
      const last = cues[cues.length - 1];
      startTime = last.endTime;
      endTime = new Date(new Date(last.endTime).getTime() + 30 * 60000).toISOString();
    }
    const newCue: Cue = {
      id: '', cueNumber: cues.length + 1, title: '',
      startTime, endTime, projectRef: projectId, isLive: isFirst, fieldValues: {},
    };
    try {
      const ref = await addDoc(collection(db, 'cues'), newCue);
      const next = [...cues, { ...newCue, id: ref.id }];
      setCues(next);
      syncProjectMeta(next);
    } catch (err) { console.error('Error adding cue:', err); }
  };

  const addField = async () => {
    if (!canEdit) return;
    if (!canUseCustomFields()) { setUpgradeFeature('customFields'); return; }
    if (!newFieldLabel.trim() || !projectId) return;
    const newField: CustomField = {
      id: `field_${Date.now()}`, label: newFieldLabel.trim(), type: newFieldType,
    };
    const updated = [...fields, newField];
    setFields(updated);
    setNewFieldLabel('');
    setNewFieldType('text');
    setShowFieldModal(false);
    await persistFields(updated);
  };

  const removeField = async (id: string) => {
    if (!canEdit) return;
    const updated = fields.filter((f) => f.id !== id);
    setFields(updated);
    await persistFields(updated);
  };

  const persistFields = async (updated: CustomField[]) => {
    if (!projectId) return;
    try { await updateDoc(doc(db, 'projects', projectId), { fields: updated }); }
    catch (err) { console.error('Error saving fields:', err); }
  };

  const handleDeleteCue = async () => {
    if (!canEdit) { setDeleteCueId(null); return; }
    if (!deleteCueId) return;
    try {
      await deleteDoc(doc(db, 'cues', deleteCueId));
      const remaining = cues
        .filter(c => c.id !== deleteCueId)
        .map((c, i) => ({ ...c, cueNumber: i + 1, isLive: i === 0 }));
      setCues(remaining);
      syncProjectMeta(remaining);
      await Promise.all(remaining.map(c =>
        updateDoc(doc(db, 'cues', c.id), { cueNumber: c.cueNumber, isLive: c.isLive })
      ));
    } catch (err) { console.error('Error deleting cue:', err); }
    finally { setDeleteCueId(null); }
  };

  const handleAIImport = async (parsedCues: ParsedCue[], newFields: CustomField[], removedFieldIds: string[] = []) => {
    if (!projectId || !project) return;
    const startCueNumber = cues.length + 1;
    const projectDate = project.date instanceof Date ? project.date : new Date(project.date);
    if (newFields.length > 0 || removedFieldIds.length > 0) {
      const updatedFields = [...fields.filter((f) => !removedFieldIds.includes(f.id)), ...newFields];
      setFields(updatedFields);
      await persistFields(updatedFields);
    }
    const created: Cue[] = [];
    for (let i = 0; i < parsedCues.length; i++) {
      const p = parsedCues[i];
      const newCue: Omit<Cue, 'id'> = {
        cueNumber: startCueNumber + i, title: p.title,
        startTime: toDateTimeString(projectDate, p.startTime),
        endTime: toDateTimeString(projectDate, p.endTime),
        projectRef: projectId, isLive: cues.length === 0 && i === 0,
        fieldValues: p.fieldValues,
      };
      const ref = await addDoc(collection(db, 'cues'), newCue);
      created.push({ ...newCue, id: ref.id });
    }
    setCues((prev) => [...prev, ...created]);
  };

  if (loading) return <LoadingScreen />;

  return (
    <div className="ci-shell">

      {/* ── Topbar ── */}
      <header className="ci-topbar">
        <div className="ci-topbar-left">
          <img src={logo} className="ci-logo" alt="LiveCue" onClick={() => navigate('/HomePage')} />
          <div className="ci-topbar-divider" />
          <div>
            <div className="ci-proj-name">{project?.title}</div>
            <div className="ci-proj-date">
              {project?.date.toLocaleDateString([], { month: 'long', day: 'numeric', year: 'numeric' })}
            </div>
          </div>
        </div>
        <div className="ci-topbar-right">
          <span className={`ci-save-status ci-save-${saveStatus}`} role="status" aria-live="polite">
            {saveStatus === 'saving' && '● Saving…'}
            {saveStatus === 'saved'  && '✓ Saved'}
            {saveStatus === 'error'  && '⚠ Error saving'}
          </span>
          <span className="ci-count-badge">{cues.length} cue{cues.length !== 1 ? 's' : ''}</span>
          <button className="ci-btn-ghost" onClick={() => canUseCustomFields() ? setShowFieldModal(true) : setUpgradeFeature('customFields')}><IconSettings size={15} /> Fields</button>
          <button className="ci-btn-ghost" onClick={() => canUseAIImport(0) ? setShowAIImport(true) : setUpgradeFeature('aiImport')}><IconUpload size={15} /> Import</button>
          <button
            className={`ci-btn-ghost${autoTiming ? ' ci-btn-ghost--on' : ''}`}
            onClick={toggleAutoTiming}
            title="When on, changing a cue's end time shifts all later cues to stay back-to-back"
          ><IconRefresh size={15} /> Auto-time: {autoTiming ? 'On' : 'Off'}</button>
          <button className="ci-btn-ghost" onClick={() => window.print()}><IconPrinter size={15} /> Print / PDF</button>
          <button className="ci-btn-ghost" onClick={() => project && exportCuesToCsv(project.title, cues, fields)}><IconDownload size={15} /> CSV</button>
          <button className="ci-btn-ghost" onClick={() => setShowHistory(true)}><IconHistory size={15} /> History</button>
          <button className="ci-btn-live" onClick={() => navigate(`/AdminPage/${projectId}`)}><IconBroadcast size={15} /> Go Live</button>
        </div>
      </header>

      {!canEdit && (
        <div className="ci-readonly-banner">
          <IconEye size={15} /> View only — your role ({teamRole}) can't edit this cue sheet.
          {teamRole === 'operator' && ' You can run the show from Go Live.'}
        </div>
      )}

      {/* ── Table (scrolls both axes) ── */}
      <div className="ci-table-wrap">
        <div className="ci-table-inner">

          {/* Column headers — sticky top */}
          <div className="ci-header-row">
            <div className="ci-col-spine ci-th-corner" />
            <div className="ci-col-title ci-th">Title</div>
            <div className="ci-col-field ci-th">Start</div>
            <div className="ci-col-field ci-th">End</div>
            <div className="ci-col-duration ci-th">Duration</div>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleColDragEnd}>
              <SortableContext items={fields.map(f => f.id)} strategy={horizontalListSortingStrategy}>
                {fields.map(f => (
                  <SortableFieldHeader key={f.id} field={f} onRemove={removeField} />
                ))}
              </SortableContext>
            </DndContext>
            <div className="ci-col-del ci-th" />
          </div>

          {/* Cue rows */}
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={cues.map(c => c.id)} strategy={verticalListSortingStrategy}>
              {cues.map((cue, index) => (
                <SortableCueRow
                  key={cue.id}
                  cue={cue}
                  index={index}
                  fields={fields}
                  isLast={index === cues.length - 1}
                  dragEnabled={canDragReorder()}
                  onInputChange={handleInputChange}
                  onTimeChange={handleTimeChange}
                  onDelete={setDeleteCueId}
                  onOpenComments={setCommentsCueId}
                  commentCount={comments.filter((c) => c.cueId === cue.id).length}
                />
              ))}
            </SortableContext>
          </DndContext>

          {/* Add cue row */}
          <div className="ci-add-row" onClick={addCue}>
            <div className="ci-col-spine ci-add-spine" />
            <div className="ci-add-label">+ Add cue</div>
          </div>

        </div>
      </div>

      {/* ── Delete confirmation ── */}
      {deleteCueId && (
        <div className="confirm-overlay" onClick={() => setDeleteCueId(null)}>
          <div className="confirm-modal" onClick={e => e.stopPropagation()}>
            <h3 className="inter-bold" style={{ color: 'var(--text-primary)', marginBottom: 10 }}>Delete Cue?</h3>
            <p className="inter-regular" style={{ color: 'var(--text-muted)', fontSize: 14, marginBottom: 24 }}>
              Are you sure you want to delete{cueToDelete?.title
                ? <> cue <strong style={{ color: 'var(--text-primary)' }}>"{cueToDelete.title}"</strong></>
                : ' this cue'}? This cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="confirm-btn-cancel" onClick={() => setDeleteCueId(null)}>Cancel</button>
              <button className="confirm-btn-delete" onClick={handleDeleteCue}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Import wizard ── */}
      {showAIImport && project && (
        <ImportWizard
          projectId={projectId!}
          projectDate={project.date instanceof Date ? project.date : new Date(project.date)}
          fields={fields}
          existingCueCount={cues.length}
          onClose={() => setShowAIImport(false)}
          onImport={handleAIImport}
        />
      )}

      {/* ── Add field modal ── */}
      {showFieldModal && (
        <div className="field-modal-overlay" onClick={() => setShowFieldModal(false)}>
          <div className="field-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="inter-bold" style={{ marginBottom: 16, color: 'var(--text-primary)' }}>Add Custom Field</h3>
            <label className="inter-medium" style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 6, display: 'block' }}>
              Field Label
            </label>
            <input
              className="field-modal-input"
              placeholder="e.g. Microphone Type"
              value={newFieldLabel}
              onChange={(e) => setNewFieldLabel(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') addField(); }}
              autoFocus
            />
            <label className="inter-medium" style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '12px 0 6px', display: 'block' }}>
              Field Type
            </label>
            <div style={{ display: 'flex', gap: 10 }}>
              <button className={`field-type-btn ${newFieldType === 'text' ? 'active' : ''}`} onClick={() => setNewFieldType('text')}>Text</button>
              <button className={`field-type-btn ${newFieldType === 'time' ? 'active' : ''}`} onClick={() => setNewFieldType('time')}>Time</button>
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
              <button className="field-modal-cancel" onClick={() => setShowFieldModal(false)}>Cancel</button>
              <button className="field-modal-add" onClick={addField}>Add Field</button>
            </div>
          </div>
        </div>
      )}

      {upgradeFeature && (
        <UpgradeModal feature={upgradeFeature} currentPlan={plan} onClose={() => setUpgradeFeature(null)} />
      )}

      {/* Comments drawer */}
      {commentsCueId && (() => {
        const cue = cues.find((c) => c.id === commentsCueId);
        const thread = comments.filter((c) => c.cueId === commentsCueId);
        return (
          <>
            <div className="ci-cmt-overlay" onClick={() => setCommentsCueId(null)} />
            <div className="ci-cmt-drawer" role="dialog" aria-modal="true" aria-label="Cue comments">
              <div className="ci-cmt-head">
                <div>
                  <div className="ci-cmt-label">COMMENTS</div>
                  <div className="ci-cmt-title">{cue ? `${cue.cueNumber}. ${cue.title || 'Untitled'}` : ''}</div>
                </div>
                <button className="ci-cmt-close" onClick={() => setCommentsCueId(null)} aria-label="Close comments"><IconX size={16} /></button>
              </div>
              <div className="ci-cmt-list">
                {thread.length === 0 && <div className="ci-cmt-empty">No comments yet. Start the conversation.</div>}
                {thread.map((c) => (
                  <div key={c.id} className="ci-cmt-item">
                    <div className="ci-cmt-item-head">
                      <span className="ci-cmt-author">{c.authorName}</span>
                      <span className="ci-cmt-time">{new Date(c.createdAt).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>
                      {auth.currentUser?.uid === c.uid && (
                        <button className="ci-cmt-del" onClick={() => deleteComment(c.id)} aria-label="Delete comment"><IconX size={13} /></button>
                      )}
                    </div>
                    <div className="ci-cmt-text">{c.text}</div>
                  </div>
                ))}
              </div>
              <div className="ci-cmt-compose">
                <textarea
                  className="ci-cmt-input"
                  placeholder="Add a comment…"
                  aria-label="Add a comment"
                  autoFocus
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); addComment(); } }}
                  rows={2}
                />
                <button className="ci-cmt-send" onClick={addComment} disabled={!commentText.trim()}>Post</button>
              </div>
            </div>
          </>
        );
      })()}

      {/* Revision history drawer */}
      {showHistory && (
        <>
          <div className="ci-cmt-overlay" onClick={() => setShowHistory(false)} />
          <div className="ci-cmt-drawer" role="dialog" aria-modal="true" aria-label="Revision history">
            <div className="ci-cmt-head">
              <div>
                <div className="ci-cmt-label">REVISION HISTORY</div>
                <div className="ci-cmt-title">Saved versions</div>
              </div>
              <button className="ci-cmt-close" onClick={() => setShowHistory(false)} aria-label="Close history"><IconX size={16} /></button>
            </div>
            <div className="ci-cmt-compose" style={{ borderTop: 'none', borderBottom: '1px solid var(--border-main, rgba(127,168,181,0.14))' }}>
              <button className="ci-cmt-send" style={{ alignSelf: 'stretch' }} onClick={saveVersion} disabled={!canEdit}>
                <IconSave size={15} /> Save current version
              </button>
            </div>
            <div className="ci-cmt-list">
              {snapshots.length === 0 && <div className="ci-cmt-empty">No saved versions yet. Save one before big changes so you can roll back.</div>}
              {snapshots.map((s) => (
                <div key={s.id} className="ci-ver-item">
                  <div className="ci-ver-info">
                    <div className="ci-ver-label">{s.label}</div>
                    <div className="ci-ver-meta">
                      {s.cues.length} cue{s.cues.length !== 1 ? 's' : ''} · {s.authorName} · {new Date(s.createdAt).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                    </div>
                  </div>
                  <button className="ci-ver-restore" onClick={() => restoreVersion(s)} disabled={restoring || !canEdit}>
                    {restoring ? '…' : 'Restore'}
                  </button>
                  <button className="ci-cmt-del" onClick={() => deleteVersion(s.id)} aria-label="Delete version"><IconX size={13} /></button>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Print / PDF (hidden on screen; shown by the print stylesheet) */}
      {project && (
        <PrintableCueSheet
          title={project.title}
          date={project.date instanceof Date ? project.date : new Date(project.date)}
          cues={cues}
          fields={fields}
        />
      )}

    </div>
  );
}

export default CueInput;
